// src/screens/KioskProfileScreen.js
import React, { useContext, useEffect, useState } from 'react';
import { AuthContext } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import AppHeader from '../components/AppHeader';
import useKioskInactivity from '../hooks/useKioskInactivity';

const KioskProfileScreen = () => {
  const { user, exitKiosk } = useContext(AuthContext);
  const navigate = useNavigate();
  // Leaving kiosk mode needs the kiosk password (checked by the API), so a
  // customer cannot turn the tablet into a normal browser session.
  const [exiting, setExiting] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // 2-minute global inactivity timer
  useKioskInactivity();

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    if (user.role !== 'kiosk') {
      navigate('/');
      return;
    }
  }, [user, navigate]);

  const handleExit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await exitKiosk(password);
      navigate('/login', { replace: true });
    } catch (err) {
      setError(err.response?.data?.message || 'Could not exit kiosk mode.');
      setPassword('');
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <AppHeader title="Kiosk Profile" />

      <div className="flex-1 overflow-y-auto p-4 pb-20 space-y-4">
        {/* Profile Info */}
        <div className="bg-app-bg-secondary-light dark:bg-app-bg-secondary rounded-2xl p-4 border border-app-border-light dark:border-app-border space-y-3">
          <div className="text-sm font-semibold text-app-text-light dark:text-app-text mb-2">
            Kiosk Information
          </div>

          <div>
            <div className="text-xs text-app-text-secondary-light dark:text-app-text-secondary">Dealership</div>
            <div className="text-sm text-app-text-light dark:text-app-text mt-1">
              {user?.dealer?.name || 'N/A'}
            </div>
          </div>

          <div>
            <div className="text-xs text-app-text-secondary-light dark:text-app-text-secondary">Kiosk ID</div>
            <div className="text-sm text-app-text-light dark:text-app-text mt-1 font-mono">
              {user?.kioskId || 'N/A'}
            </div>
          </div>
        </div>

        {/* Exit kiosk mode (staff only: needs the kiosk password) */}
        {!exiting ? (
          <button
            onClick={() => setExiting(true)}
            className="w-full py-3 rounded-2xl bg-red-500/20 border border-red-500/30 text-red-400 text-sm font-semibold hover:bg-red-500/30"
          >
            Exit kiosk mode
          </button>
        ) : (
          <form
            onSubmit={handleExit}
            className="bg-app-bg-secondary-light dark:bg-app-bg-secondary rounded-2xl p-4 border border-app-border-light dark:border-app-border space-y-3"
          >
            <label className="block text-sm font-semibold text-app-text-light dark:text-app-text" htmlFor="kiosk-exit-password">
              Staff only: enter the kiosk password
            </label>
            <input
              id="kiosk-exit-password"
              type="password"
              autoComplete="off"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-3 rounded-xl border border-app-input-border-light dark:border-app-input-border bg-white dark:bg-app-input text-sm"
            />
            {error && <p className="text-red-400 text-xs">{error}</p>}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => { setExiting(false); setPassword(''); setError(''); }}
                className="flex-1 py-3 rounded-2xl bg-app-bg-light dark:bg-app-bg text-sm font-semibold"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!password || submitting}
                className="flex-1 py-3 rounded-2xl bg-red-500 text-white text-sm font-semibold disabled:opacity-50"
              >
                {submitting ? 'Checking...' : 'Exit kiosk mode'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default KioskProfileScreen;
